// import { DeleteIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  ButtonGroup,
  Separator,
  Flex,
  IconButton,
  Popover,
  Stack,
  Text,
  useDisclosure,
  NativeSelect,
  Icon,
} from "@chakra-ui/react";
import { t } from "i18next";
import { observer } from "mobx-react";
import { ChangeEvent, useState, useEffect } from "react";
import { SelectFilter } from "./attribute-filters/SelectFilter";
import { FilterTag } from "./FilterTag";
import { TextAreaFilter } from "./attribute-filters/TextAreaFilter";
import {
  AttributeDefinition,
  AttributeDefinitions,
  AttributeName,
  AttributeType,
} from "@keita/lanepatrol-sdk";

import { AttributeUtils } from "../../../../coding/utils/attribute-utils";
import {
  attributeFilterToString,
  NumberAttributeFilter,
  AttributeFilter,
  AttributeFilterType,
  SelectAttributeFilter,
  TextAreaAttributeFilter,
} from "../../../../map/models/MapFilters";
import { useStores } from "../../../../shared/stores/RootStore";
import { CustomPopover } from "../../../../shared/views/CustomPopover";
import { TextFilter } from "./attribute-filters/TextFilter";
import { DeleteIcon } from "../../../../shared/views/icons";

const AttributeFiltersBase: React.FC = () => {
  const { filterStore } = useStores();
  const { open, onToggle, onClose } = useDisclosure();
  const [attributeFilters, setAttributeFilters] = useState<AttributeFilter[]>(
    []
  );

  useEffect(() => {
    if (open) {
      setAttributeFilters([...filterStore.attributeFilters]);
    }
    //eslint-disable-next-line
  }, [open]);

  const handleAddFilter = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as AttributeName;
    const attribute = AttributeDefinitions.getByName(value)!;
    const filter = getDefaultFilter(attribute);
    setAttributeFilters((prev) => {
      return prev.concat([filter]);
    });
  };

  const handleClear = () => {
    setAttributeFilters([]);
  };

  const handleSubmit = () => {
    filterStore.setAttributeFilters(
      attributeFilters.filter((filter) => {
        if (filter.type === AttributeFilterType.Number) {
          const f = filter as NumberAttributeFilter;
          if (!f.min && !f.max) return false;
        } else if (filter.type === AttributeFilterType.Select) {
          const f = filter as SelectAttributeFilter;
          if (!f.value) return false;
        }
        return true;
      })
    );
    onClose();
  };

  const handleChangeFilter = (filterIndex: number, data: any) => {
    setAttributeFilters((prev) =>
      prev.map((f, i) => (i === filterIndex ? { ...f, ...data } : f))
    );
  };

  const handleRemoveFilter = (filterIndex: number) => {
    setAttributeFilters((prev) => prev.filter((_, i) => i !== filterIndex));
  };

  const removeActiveFilter = (attrName: string) => {
    const newFilters = filterStore.attributeFilters.filter(
      (filter) => filter.attributeName !== attrName
    );
    filterStore.setAttributeFilters(newFilters);
  };

  return (
    <Stack>
      <CustomPopover
        open={open}
        onClose={onClose}
        maxH="360px"
        minW="360px"
        trigger={
          <Button
            fontSize={"sm"}
            size="sm"
            variant="outline"
            colorPalette="primary"
            h="unset"
            py={1.5}
            onClick={onToggle}
          >
            {t("map:filters.byAttributeName")}
          </Button>
        }
      >
        <Popover.Header fontWeight={"semibold"}>
          {t("map:filters.byAttributeTitle")}
        </Popover.Header>
        <Popover.Body overflowY="auto">
          <div>
            <NativeSelect.Root mt={2} variant="outline">
              <NativeSelect.Field
                placeholder="Add attribute filter"
                onChange={handleAddFilter}
                value={""}
              >
                {AttributeDefinitions.all.map((attribute, i) => (
                  <option key={i} value={attribute.name}>
                    {AttributeUtils.getLabel(attribute.name)}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </div>
          <div>
            {[...attributeFilters].map((filter, i) => {
              const attributeDefinition = AttributeDefinitions.getByName(
                filter.attributeName
              )!;
              return (
                <div key={i}>
                  <Separator mt={4} />
                  <Flex alignItems="center" mt={2} mb={2}>
                    <Text fontSize={"sm"} flexGrow={1}>
                      {AttributeUtils.getLabel(attributeDefinition.name)}
                    </Text>
                    <IconButton
                      variant="ghost"
                      colorPalette="primary"
                      onClick={() => handleRemoveFilter(i)}
                      size="sm"
                      aria-label="delete"
                    >
                      <Icon as={DeleteIcon} />
                    </IconButton>
                  </Flex>
                  {getFilteringComponent(filter, (data: any) =>
                    handleChangeFilter(i, data)
                  )}
                </div>
              );
            })}
          </div>
        </Popover.Body>
        <Popover.Footer display="flex" justifyContent="flex-end">
          <ButtonGroup colorPalette={"primary"} size={"sm"}>
            <Button onClick={handleClear} disabled={!attributeFilters.length}>
              {t("map:filters.clearButton")}
            </Button>
            <Button onClick={handleSubmit}>
              {t("map:filters.submitButton")}
            </Button>
          </ButtonGroup>
        </Popover.Footer>
      </CustomPopover>

      {!!filterStore.attributeFilters.length && (
        <Box m={-1}>
          {filterStore.attributeFilters.map((attributeFilter, i) => {
            return (
              <FilterTag
                key={i}
                onClose={() =>
                  removeActiveFilter(attributeFilter.attributeName)
                }
              >
                {`${AttributeUtils.getLabel(
                  attributeFilter.attributeName
                )}: ${attributeFilterToString(attributeFilter)}`}
              </FilterTag>
            );
          })}
        </Box>
      )}
    </Stack>
  );
};

export const AttributeFilters = observer(AttributeFiltersBase);

const getDefaultFilter = (attribute: AttributeDefinition): AttributeFilter => {
  switch (attribute.type) {
    case AttributeType.Number:
      const filter: NumberAttributeFilter = {
        attributeName: attribute.name,
        type: AttributeFilterType.Number,
        min: null,
        max: null,
      };
      return filter;

    case AttributeType.TextArea:
      return {
        attributeName: attribute.name,
        type: AttributeFilterType.TextArea,
        value: true,
      } as TextAreaAttributeFilter;

    case AttributeType.Select:
      return {
        attributeName: attribute.name,
        type: AttributeFilterType.Select,
      } as SelectAttributeFilter;

    default:
      throw new Error("Filtering is unavailable for the requested attribute.");
  }
};

const getFilteringComponent = (
  filter: AttributeFilter,
  setValue: (data: any) => void
): React.ReactNode => {
  if (filter.type === AttributeFilterType.Number) {
    return (
      <TextFilter
        attributeFilter={filter as NumberAttributeFilter}
        setValue={setValue}
      />
    );
  } else if (filter.type === AttributeFilterType.TextArea) {
    return (
      <TextAreaFilter
        attributeFilter={filter as TextAreaAttributeFilter}
        setValue={setValue}
      />
    );
  } else {
    return (
      <SelectFilter
        attributeFilter={filter as SelectAttributeFilter}
        setValue={setValue}
      />
    );
  }
};
